import React from "react";
import { Link } from "react-router-dom";
import { HardHat, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "June 10, 2026";
const COMPANY_NAME   = "Builders Binder";
const CONTACT_EMAIL  = "support@buildersbinder.com";
const SITE_URL       = "https://buildersbinder.com";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white font-sans text-zinc-800">
      {/* Nav */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-500 rounded-sm flex items-center justify-center">
              <HardHat size={15} className="text-zinc-950" />
            </div>
            <span className="font-bold text-sm tracking-tight">{COMPANY_NAME}</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Legal</p>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Terms of Service</h1>
          <p className="text-sm text-zinc-500">Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {EFFECTIVE_DATE}</p>
        </div>

        <div className="prose prose-zinc max-w-none space-y-8 text-[15px] leading-relaxed">

          <section>
            <p>
              Please read these Terms of Service ("<strong>Terms</strong>") carefully before using{" "}
              <a href={SITE_URL} className="underline text-zinc-700">{SITE_URL}</a> and any related
              applications or services (collectively, the "<strong>Service</strong>") operated by{" "}
              <strong>{COMPANY_NAME}</strong> ("<strong>we</strong>," "<strong>our</strong>," or "<strong>us</strong>").
            </p>
            <p className="mt-3">
              By creating an account or using the Service, you agree to be bound by these Terms and our{" "}
              <Link to="/privacy" className="underline text-zinc-700">Privacy Policy</Link>, which is
              incorporated herein by reference. If you do not agree, do not use the Service.
            </p>
          </section>

          <Section title="1. Eligibility">
            <p>
              You must be at least 18 years old and have the legal authority to enter into a binding contract to use
              the Service. If you are using the Service on behalf of a business entity, you represent that you have
              authority to bind that entity to these Terms.
            </p>
          </Section>

          <Section title="2. Account Registration and Security">
            <ul className="list-disc pl-5 space-y-1">
              <li>You must provide accurate, current, and complete information during registration and keep it up to date.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</li>
              <li>You must notify us immediately at <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a> if you suspect unauthorized access to your account.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms or that we reasonably suspect are involved in fraudulent or abusive activity.</li>
              <li>You may not create more than one account per person, or share your account with others.</li>
            </ul>
          </Section>

          <Section title="3. Permitted Use and Restrictions">
            <p>The Service is provided solely for lawful, business-related purposes related to monitoring New York City Department of Buildings permit data. You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations.</li>
              <li>Attempt to reverse-engineer, decompile, disassemble, or derive the source code of the Service.</li>
              <li>Scrape, crawl, or systematically extract data from the Service beyond normal use.</li>
              <li>Circumvent rate limits, access controls, or other security mechanisms.</li>
              <li>Use the Service to transmit malware, spam, or other harmful content.</li>
              <li>Resell, sublicense, or otherwise commercialize the Service or its data without our written consent.</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service or its infrastructure.</li>
            </ul>
          </Section>

          <Section title="3A. Contractor Responsibility and No Professional Advice">
            <p>
              The Service is a workflow, search, dashboard, and notification tool for DOB NOW, NYC Open Data,
              NYC GeoSearch, permit, filing, inspection, DOB violation, and ECB violation information. It is not
              a law firm, expeditor, architect, engineer, filing representative, special inspection agency,
              licensed professional, insurance provider, or government agency. We do not provide legal,
              regulatory, permitting, safety, engineering, architectural, expediting, financial, or professional
              advice.
            </p>
            <p className="mt-2">
              You remain solely responsible for complying with all applicable laws, DOB rules, permit conditions,
              inspection requirements, violation deadlines, ECB payment obligations, jobsite safety obligations,
              license requirements, contract deadlines, and client obligations. You are responsible for independently
              verifying all time-sensitive or compliance-critical information with official NYC DOB, OATH/ECB, and
              other applicable government channels.
            </p>
          </Section>

          <Section title="4. Subscription Plans and Billing">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Free trial:</strong> new accounts receive a 7-day free trial with limited features. No credit
                card is required for the trial. Unused trial days are not carried over upon upgrade.
              </li>
              <li>
                <strong>Paid plans:</strong> paid subscriptions are billed monthly in advance at the rate displayed at
                the time of purchase. All amounts are in US dollars.
              </li>
              <li>
                <strong>Payment processing:</strong> payments are processed by Paddle.com Market Limited. By providing payment
                information, you authorize us to charge your payment method on a recurring basis until you cancel.
              </li>
              <li>
                <strong>Renewals:</strong> subscriptions renew automatically at the end of each billing period unless
                cancelled before the renewal date.
              </li>
              <li>
                <strong>Cancellation:</strong> you may cancel your subscription at any time via the Paddle billing
                portal in Account Settings. Cancellation takes effect at the end of the current paid period; you will
                retain access until then.
              </li>
              <li>
                <strong>Refunds:</strong> except where required by applicable law, all fees are non-refundable.
                If you believe you were charged in error, contact us within 30 days of the charge at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a>.
              </li>
              <li>
                <strong>Price changes:</strong> we reserve the right to change subscription prices. We will provide
                at least 30 days' advance notice of any price increase. Your continued use of the Service after the
                effective date of a price change constitutes your acceptance of the new price.
              </li>
              <li>
                <strong>Taxes:</strong> you are responsible for all applicable taxes, levies, or duties imposed by
                taxing authorities. We will add applicable taxes to the charges where required.
              </li>
            </ul>
          </Section>

          <Section title="5. Data Accuracy, Delays, and No Warranty on Permit Data">
            <p>
              The Service retrieves permit, job filing, inspection, DOB violation, ECB violation, address, BIN, BBL,
              and related information from publicly available New York City government data sources, including DOB NOW,
              NYC Open Data APIs, NYC GeoSearch, and related public records. We do not guarantee the accuracy,
              completeness, timeliness, availability, or reliability of this information. <strong>Public records may be
              delayed, incomplete, duplicated, corrected, removed, or inconsistent across government systems. Always
              verify critical permit, filing, violation, payment, and inspection information directly with official
              NYC DOB, OATH/ECB, or other applicable government sources.</strong>
            </p>
            <p className="mt-2">
              We are not affiliated with, endorsed by, or an official service of the New York City Department of
              Buildings, OATH/ECB, NYC Open Data, or any government agency. Public-record data is provided for
              informational convenience only.
            </p>
          </Section>

          <Section title="5A. Notification and Alert Disclaimer">
            <p>
              The Service provides automated email notifications about permits, job filings, inspections,
              DOB violations, ECB violations, stop-work orders, payment-related signals, and other public-record
              activity as a convenience feature. <strong>Notifications are provided on a best-effort basis only and
              must not be relied upon as the sole source of permit, filing, violation, payment, deadline, or inspection
              information.</strong>
            </p>
            <p className="mt-2">
              We expressly disclaim any liability for: (a) alerts that are delayed, undelivered, or delivered
              out of order due to factors including, but not limited to, third-party email delivery
              infrastructure failures, spam classification, network outages, rate limiting,
              or incorrect contact information on your account; (b) any action taken or not taken by you in
              reliance on an alert or the absence of an alert; and (c) any permit, inspection, violation,
              ECB payment issue, fine, penalty, failed inspection, stop-work impact, lapse, delay, lost job,
              lost revenue, lost profit, contract damages, business interruption, or business consequence
              resulting from a missed, late, inaccurate, duplicated, or failed notification.
            </p>
            <p className="mt-2">
              <strong>You are solely responsible for independently monitoring permit and inspection status through
              official NYC DOB channels and for not relying exclusively on our notification service for
              time-sensitive or compliance-critical information.</strong> No claim arising from a missed,
              delayed, or failed alert — regardless of cause — shall give rise to any liability on our part
              beyond the limitations set forth in Section 9 of these Terms.
            </p>
          </Section>

            <p className="mt-2">
              This responsibility includes independently monitoring DOB NOW, BIS, OATH/ECB, payment portals,
              inspection calendars, emails from government agencies, and any other official channel that may affect
              your work. You agree that no missed, delayed, inaccurate, duplicated, or failed alert creates liability
              for lost revenue, lost profit, contract damages, fines, penalties, stop-work impacts, failed inspections,
              or other contractor losses.
            </p>

          <Section title="6. Intellectual Property">
            <p>
              The Service and its original content (excluding third-party government data), features, and
              functionality are owned by {COMPANY_NAME} and protected by copyright, trademark, and other
              intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of the
              Service without our prior written consent.
            </p>
            <p className="mt-2">
              You retain ownership of the job addresses and other content you submit to the Service. By submitting
              content, you grant us a limited, non-exclusive, royalty-free license to use, store, and display that
              content solely to operate and improve the Service.
            </p>
          </Section>

          <Section title="7. Third-Party Services">
            <p>
              The Service integrates with third-party services including Google (authentication), Paddle (payments),
              transactional email providers, hosting providers, NYC GeoSearch,
              DOB NOW, NYC Open Data APIs, and other public-record sources. Your use of these third-party services is
              governed by their respective terms and privacy policies. We are not responsible for the availability,
              accuracy, uptime, delivery performance, security, or practices of third-party services.
            </p>
          </Section>

          <Section title="8. Disclaimer of Warranties">
            <p className="uppercase font-semibold text-sm text-zinc-700">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
              NON-INFRINGEMENT, OR UNINTERRUPTED OR ERROR-FREE OPERATION. WE DO NOT WARRANT THAT THE SERVICE WILL
              MEET YOUR REQUIREMENTS OR THAT PERMIT, FILING, VIOLATION, PAYMENT, OR INSPECTION DATA WILL BE
              ACCURATE, COMPLETE, OR UP TO DATE.
            </p>
          </Section>

          <Section title="9. Limitation of Liability">
            <p className="uppercase font-semibold text-sm text-zinc-700">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {COMPANY_NAME.toUpperCase()},
              ITS DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY
              OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR
              IN CONNECTION WITH YOUR ACCESS TO OR USE OF (OR INABILITY TO USE) THE SERVICE OR ANY THIRD-PARTY
              CONTENT, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p className="mt-3 uppercase font-semibold text-sm text-zinc-700">
              WITHOUT LIMITING THE FOREGOING, {COMPANY_NAME.toUpperCase()} SHALL HAVE NO LIABILITY WHATSOEVER
              FOR ANY CLAIM ARISING FROM OR RELATED TO: (A) THE FAILURE, DELAY, OR NON-DELIVERY OF ANY
              NOTIFICATION OR ALERT, REGARDLESS OF CAUSE; (B) INACCURATE, INCOMPLETE, OR OUTDATED PERMIT, FILING,
              VIOLATION, PAYMENT, OR INSPECTION DATA SOURCED FROM THIRD-PARTY GOVERNMENT DATABASES; (C) ANY BUSINESS
              DECISION, REGULATORY VIOLATION, PERMIT LAPSE, ECB PAYMENT ISSUE, FINE, PENALTY, FAILED INSPECTION,
              STOP-WORK IMPACT, LOST REVENUE, LOST PROFIT, CONTRACT DAMAGES, OR LOSS RESULTING FROM YOUR RELIANCE
              ON THE SERVICE AS YOUR SOLE SOURCE OF PERMIT, FILING, VIOLATION, PAYMENT, OR INSPECTION INFORMATION;
              OR (D) ANY FAILURE OF THIRD-PARTY EMAIL, HOSTING, PAYMENT, OR GOVERNMENT DATA
              PROVIDERS.
            </p>
            <p className="mt-3 uppercase font-semibold text-sm text-zinc-700">
              IN ANY CASE, OUR AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE USE
              OF OR INABILITY TO USE THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE TOTAL FEES PAID BY YOU
              TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED US DOLLARS ($100).
            </p>
          </Section>

          <Section title="10. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless {COMPANY_NAME} and its officers, directors,
              employees, and agents from and against any and all claims, liabilities, damages, losses, and expenses
              (including reasonable attorneys' fees) arising out of or in any way connected with (a) your use of
              the Service; (b) your violation of these Terms; (c) your violation of any third-party rights; or
              (d) any content you submit through the Service.
            </p>
          </Section>

          <Section title="11. Termination">
            <p>
              You may terminate your account at any time by contacting us or using the account deletion option in
              your settings. We may suspend or terminate your account at any time, with or without cause, with or
              without notice, if we determine you have violated these Terms or for any other reason at our discretion.
            </p>
            <p className="mt-2">
              Upon termination, your right to use the Service will immediately cease. Sections 5, 5A, 6, 8, 9, 10, 12,
              and 13 will survive termination.
            </p>
          </Section>

          <Section title="12. Governing Law and Dispute Resolution">
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of New York, United
              States, without regard to its conflict-of-law principles.
            </p>
            <p className="mt-2">
              <strong>Informal resolution:</strong> before filing a formal claim, you agree to contact us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a> and
              attempt to resolve the dispute informally within 30 days.
            </p>
            <p className="mt-2">
              <strong>Binding arbitration:</strong> if informal resolution fails, any dispute arising out of or
              relating to these Terms or the Service shall be resolved by binding arbitration administered by the
              American Arbitration Association (AAA) under its Consumer Arbitration Rules. The arbitration shall
              take place in New York, New York. Each party shall bear its own costs and attorneys' fees unless
              the arbitrator determines a party acted in bad faith.
            </p>
            <p className="mt-2">
              <strong>Class action waiver:</strong> you agree that any dispute resolution proceedings will be
              conducted only on an individual basis and not in a class, consolidated, or representative action.
            </p>
            <p className="mt-2">
              <strong>Exceptions:</strong> either party may seek injunctive or other equitable relief in any court
              of competent jurisdiction for matters involving intellectual property, confidentiality, or unauthorized
              access to the Service.
            </p>
          </Section>

          <Section title="13. Changes to Terms">
            <p>
              We reserve the right to modify these Terms at any time. When we make material changes, we will provide
              at least 14 days' advance notice by email or a prominent in-app notice before the changes take effect.
              Your continued use of the Service after the effective date of revised Terms constitutes your acceptance
              of the changes. If you do not agree to the new Terms, you must stop using the Service before the
              effective date.
            </p>
          </Section>

          <Section title="14. General">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Entire agreement:</strong> these Terms, together with the Privacy Policy, constitute the entire agreement between you and us regarding the Service and supersede all prior agreements.</li>
              <li><strong>Severability:</strong> if any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.</li>
              <li><strong>Waiver:</strong> our failure to enforce any right or provision will not be considered a waiver of those rights.</li>
              <li><strong>Assignment:</strong> you may not assign these Terms without our prior written consent. We may assign our rights and obligations without restriction.</li>
              <li><strong>Force majeure:</strong> we are not liable for any failure or delay resulting from events beyond our reasonable control, including natural disasters, government actions, or third-party service outages.</li>
              <li><strong>No agency:</strong> these Terms do not create any agency, partnership, joint venture, or employment relationship between the parties.</li>
            </ul>
          </Section>

          <Section title="15. Contact Us">
            <p>Questions about these Terms? Reach us at:</p>
            <address className="not-italic mt-2 text-sm bg-zinc-50 border border-zinc-200 rounded-sm p-4">
              <strong>{COMPANY_NAME}</strong><br />
              Email: <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a><br />
              Website: <a href={SITE_URL} className="underline text-zinc-700">{SITE_URL}</a>
            </address>
          </Section>

        </div>
      </main>

      <footer className="border-t border-zinc-200 py-8 mt-12 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-zinc-500">
          <span>© {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.</span>
          <nav className="flex gap-5">
            <Link to="/privacy" className="hover:text-zinc-950 underline underline-offset-2">Privacy Policy</Link>
            <Link to="/terms"   className="hover:text-zinc-950 underline underline-offset-2">Terms of Service</Link>
            <Link to="/refunds" className="hover:text-zinc-950 underline underline-offset-2">Refund Policy</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-zinc-950">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-bold tracking-tight text-zinc-950 border-b border-zinc-100 pb-2">{title}</h2>
      {children}
    </section>
  );
}
